/**
 * A* pathfinding on the isometric tile grid.
 * Pure logic — no rendering dependencies.
 */

export interface PathNode {
  x: number;
  y: number;
}

interface AStarNode {
  x: number;
  y: number;
  g: number; // cost from start
  h: number; // heuristic to end
  f: number; // g + h
  parent: AStarNode | null;
}

function heuristic(ax: number, ay: number, bx: number, by: number): number {
  return Math.abs(ax - bx) + Math.abs(ay - by); // Manhattan distance
}

/**
 * Find a path from start to end on the grid.
 * @param startX, startY - Start tile coordinates
 * @param endX, endY - End tile coordinates
 * @param gridWidth, gridHeight - Grid dimensions
 * @param blocked - Set of "x,y" strings for blocked tiles
 * @returns Array of path nodes from start to end (inclusive), or null if no path
 */
export function findPath(
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  gridWidth: number,
  gridHeight: number,
  blocked: Set<string> = new Set(),
): PathNode[] | null {
  if (startX === endX && startY === endY) return [{ x: startX, y: startY }];

  const key = (x: number, y: number) => `${x},${y}`;

  if (blocked.has(key(endX, endY))) return null;

  const open: AStarNode[] = [];
  const closed = new Set<string>();

  const start: AStarNode = {
    x: startX,
    y: startY,
    g: 0,
    h: heuristic(startX, startY, endX, endY),
    f: heuristic(startX, startY, endX, endY),
    parent: null,
  };
  open.push(start);

  // 4-directional movement (no diagonals in iso grid)
  const dirs = [
    { dx: 0, dy: -1 }, // up
    { dx: 1, dy: 0 },  // right
    { dx: 0, dy: 1 },  // down
    { dx: -1, dy: 0 }, // left
  ];

  while (open.length > 0) {
    // Find node with lowest f
    let bestIdx = 0;
    for (let i = 1; i < open.length; i++) {
      if (open[i]!.f < open[bestIdx]!.f) bestIdx = i;
    }
    const current = open.splice(bestIdx, 1)[0]!;

    if (current.x === endX && current.y === endY) {
      // Reconstruct path
      const path: PathNode[] = [];
      let node: AStarNode | null = current;
      while (node) {
        path.unshift({ x: node.x, y: node.y });
        node = node.parent;
      }
      return path;
    }

    closed.add(key(current.x, current.y));

    for (const dir of dirs) {
      const nx = current.x + dir.dx;
      const ny = current.y + dir.dy;

      if (nx < 0 || nx >= gridWidth || ny < 0 || ny >= gridHeight) continue;
      if (closed.has(key(nx, ny))) continue;
      if (blocked.has(key(nx, ny))) continue;

      const g = current.g + 1;
      const h = heuristic(nx, ny, endX, endY);
      const f = g + h;

      // Check if already in open with a better score
      const existing = open.find((n) => n.x === nx && n.y === ny);
      if (existing && existing.g <= g) continue;

      if (existing) {
        existing.g = g;
        existing.h = h;
        existing.f = f;
        existing.parent = current;
      } else {
        open.push({ x: nx, y: ny, g, h, f, parent: current });
      }
    }
  }

  return null; // No path found
}
